import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import bankLogo from "../../../../assets/svg/svg_banks";
import { banksFormat } from "../../../../utils/bankList";
import LastBankUpdate from "src/components/lastBankUpdate";
import { formatMoneyWithCurrency } from 'src/utils/formatter';
import CustomModal from "src/components/configuracion/components/customModal";
import { CreateBankBalance } from "src/graphql/bankBalances";
import { SetPasswordIncludeInCashFlow } from "src/graphql/includeInCashFlow";
import { Input, Label } from "src/components/input/input";
import { importUpdateBalanceNeeded, importUpdateCashflowNeeded, importUpdateNeeded } from "src/redux/actions/flujocaja";
import { useDispatch } from "react-redux";
import useBlockedFeatures from "src/hooks/useBlockedFeatures";
import ToolbarToggleSwitch from "src/components/shared/ToolbarToggleSwitch/ToolbarToggleSwitch";
import FeatherIcon from "feather-icons-react";

export default function BankCard({ initialBalances, toMoney, companyId, showCompanyName = false }) {
  const { t } = useTranslation("finance");
  const dispatch = useDispatch();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedBank, setSelectedBank] = useState(null);
  const [newBalance, setNewBalance] = useState('');
  const [newCreditLine, setNewCreditLine] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [togglingPasswordId, setTogglingPasswordId] = useState(null);

  const { executeMutation } = CreateBankBalance();
  const { executeMutation: setIncludeInCashFlow } = SetPasswordIncludeInCashFlow();
  const { canEditarSaldosFlujoCaja } = useBlockedFeatures();
  const canEdit = canEditarSaldosFlujoCaja();

  const handleEditClick = (bank) => {
    setSelectedBank(bank);
    setNewBalance(bank.availableBalance?.toString() || '');
    setNewCreditLine(bank.creditLineAvailable?.toString() || '');
    setEditModalOpen(true);
  };

  const handleToggleIncludeInCashFlow = async (bank, checked) => {
    if (!bank?.passwordId) return;
    setTogglingPasswordId(bank.passwordId);
    try {
      const result = await setIncludeInCashFlow({
        passwordId: bank.passwordId,
        includeInCashFlow: checked,
      });
      if (result.data?.setPasswordIncludeInCashFlow) {
        dispatch(importUpdateBalanceNeeded({ value: true }));
        dispatch(importUpdateCashflowNeeded({ value: true, reload: true }));
      } else if (result.error) {
        console.error('Error updating includeInCashFlow:', result.error);
      }
    } catch (error) {
      console.error('Error updating includeInCashFlow:', error);
    } finally {
      setTogglingPasswordId(null);
    }
  };

  const handleUpdateBankBalance = async () => {
    const targetCompanyId = selectedBank?.companyId || companyId;
    if (!selectedBank || !targetCompanyId) return;
    setIsUpdating(true);
    try {
      const data = {
        companyId: targetCompanyId,
        bankId: selectedBank.bankId,
        passwordId: selectedBank.passwordId || '',
        userId: selectedBank.userId || '',
        name: selectedBank.name || banksFormat[selectedBank.bankId] || selectedBank.bankId,
        accountNumber: selectedBank.accountNumber || '',
        currency: selectedBank.currencyType || 'CLP',
        accountType: selectedBank.accountType || 'corriente',
        availableBalance: parseFloat(String(newBalance).replace(',', '.')) || 0,
        creditLineAvailable: parseFloat(String(newCreditLine).replace(',', '.')) || 0,
        amountApproved: selectedBank.amountApproved || 0,
        amountUsed: selectedBank.amountUsed || 0
      }

      const bankBalanceData = [{
        ...data
      }];

      const result = await executeMutation({
        companyId: targetCompanyId,
        data: bankBalanceData
      });

      if (result.data?.createBankBalance?.status) {
        setEditModalOpen(false);
        setSelectedBank(null);
        setNewBalance('');
        setNewCreditLine('');

        dispatch(importUpdateBalanceNeeded({ value: true }))
        dispatch(importUpdateNeeded({ value: true }));
      } else {
        console.error('Error updating bank balance:', result.error);
      }
    } catch (error) {
      console.error('Error updating bank balance:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const modalContent = (
    <div key={selectedBank?.passwordId || 'bank-edit'}>
      <div className="form-group">
        <Label value="Actualizar saldo" />
        <Input
          type="number"
          step="0.01"
          defaultValue={newBalance}
          onChange={(e) => setNewBalance(e.target.value)}
          placeholder="Ingrese el nuevo saldo"
        />
      </div>
      <div className="form-group">
        <Label value="Actualizar línea de crédito" />
        <Input
          type="number"
          defaultValue={newCreditLine}
          onChange={(e) => setNewCreditLine(e.target.value)}
          placeholder="Ingrese la nueva línea de crédito"
        />
      </div>
    </div>
  );

  return (
    <>
      {initialBalances?.data?.allBankBalances?.map((bank, index) => {
        const logoBank = bankLogo(bank.bankId);
        const includedInCashFlow = bank?.includeInCashFlow !== false;
        const inactiveConnection = bank?.isActive !== null && !bank?.isActive;
        const cardClass = [
          'initialBalanceContainer__card',
          inactiveConnection ? 'deactivatedBankCard' : '',
          !includedInCashFlow ? 'excludedFromCashFlowCard' : '',
        ].filter(Boolean).join(' ');

        return (
          <div className={cardClass} key={bank.passwordId || index}>
            <div className="initialBalanceContainer__card__imgContainer">
              <img
                className="initialBalanceContainer__card__imgContainer__img"
                src={logoBank}
                alt={`${bank.bankId}`}
                style={{height: "90px"}}
              />
            </div>
            <div className="initialBalanceContainer__card__bankDataContainer">
              {showCompanyName && bank.companyName && (
                <div className="initialBalanceContainer__card__bankDataContainer__companyName">
                  {bank.companyName}
                </div>
              )}
              <div className="initialBalanceContainer__card__bankDataContainer__bankData">
                { banksFormat[bank.bankId] || bank.bankId }
                {!includedInCashFlow && (
                  <span className="excludedFromCashFlowBadge">
                    {t("cashflow.hiddenInCashFlow")}
                  </span>
                )}
              </div>
              <div className="initialBalanceContainer__card__bankDataContainer__bankData__ctaNmbr">
                {bank?.accountType && bank?.accountType == "chequera"
                  ? "Chequera Electrónica"
                  : bank?.accountType && bank?.accountType == "pyme" ? "Cuenta PYME" : "Cuenta Corriente"}
              </div>
              <div className="initialBalanceContainer__card__bankDataContainer__bankData__ctaNmbr">
                {bank.accountNumber || "-"}
              </div>
              {canEdit && (
                <div className="initialBalanceContainer__card__includeToggle">
                  <ToolbarToggleSwitch
                    label={t("cashflow.includeInCashFlow")}
                    checked={includedInCashFlow}
                    disabled={!bank.passwordId || togglingPasswordId === bank.passwordId}
                    onChange={(checked) => handleToggleIncludeInCashFlow(bank, checked)}
                    className="initialBalanceContainer__card__includeToggleSwitch"
                  />
                  <button
                    type="button"
                    className="initialBalanceContainer__card__editBtn"
                    onClick={() => handleEditClick(bank)}
                    title={t("cashflow.update", { defaultValue: "Editar" })}
                    aria-label={t("cashflow.update", { defaultValue: "Editar" })}
                  >
                    <FeatherIcon icon="edit-2" size={16} />
                  </button>
                </div>
              )}
            </div>
            <div className="initialBalanceContainer__card__amountContainer">
              <div className="initialBalanceContainer__card__amountContainer__availableAmount">
                <div className="initialBalanceContainer__card__amountContainer__availableAmount__title">
                  Saldo
                </div>
                <div className="initialBalanceContainer__card__amountContainer__lastUpdateComponent">
                    <LastBankUpdate bank={bank} bankName={banksFormat[bank.bankId] || bank.bankId} />
                  </div>
                <div className="initialBalanceContainer__card__amountContainer__availableAmount__amount">
                  {`${formatMoneyWithCurrency(bank.availableBalance, bank.currencyType) || 0}`}
                </div>
              </div>
              <div className="initialBalanceContainer__card__amountContainer__creditAmount">
                <div className="initialBalanceContainer__card__amountContainer__creditAmount__title">
                  Línea de Crédito
                </div>
                <div className="initialBalanceContainer__card__amountContainer__creditAmount__amount">
                  {`${formatMoneyWithCurrency(bank.creditLineAvailable) || 0}`}
                </div>
              </div>
            </div>
          </div>
        );
      })}

      <CustomModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title={`Cuenta ${selectedBank ? (banksFormat[selectedBank.bankId] || selectedBank.bankId) : 'Banco'} #${selectedBank?.accountNumber} (${selectedBank?.currencyType || 'CLP'})`}
        content={modalContent}
        onConfirm={handleUpdateBankBalance}
        confirmLabel="Actualizar"
        isLoading={isUpdating}
        size="small"
      />
    </>
  );
}
